// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Reminders and emails (access-ending notice + extend), § Portal routes
// ==============================================
// /papers/extend/<token> — the one-tap link in "Tony asks to keep access to
// your papers". Signed in already: Approve / Decline, one tap. Not signed in:
// the same 6-digit email code as the request link, then the two buttons. An
// answered, replaced or expired ask gets one line saying so. Phone first
// (390 px), nothing under 13 px (readability.test.ts scans this folder).
// ==============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/AuthProvider';
import { apiError, waitLabel } from '../apiError';
import type { ApiErrorInfo } from '../apiError';
import type { ExtensionView } from '../types';
import { firstName, fmtDay, fmtDayYear, passageLabel } from '../dates';
import { C, CrewShell, ErrorNote, Loading, PrimaryButton, SecondaryButton, StepHeading, Tick } from '../ui';

type Phase = 'loading' | 'invalid' | 'fatal' | 'answer' | 'landing' | 'code' | 'wrong';

const withWait = (e: ApiErrorInfo) => (e.status === 429 && e.retryAfter ? `${e.message}. Try again in ${waitLabel(e.retryAfter)}.` : e.message);

export function ExtendAccessPage() {
  const { token = '' } = useParams();
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<ExtensionView | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [masked, setMasked] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'approve' | 'decline' | 'code' | 'send' | 'out'>(null);
  const [code, setCode] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);
  const lastSubmitted = useRef<string | null>(null);

  const fail = useCallback((e: ApiErrorInfo, where: Phase) => {
    if (e.status === 404) { setPhase('invalid'); return; }
    setError(withWait(e));
    setPhase(where);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.extendView(token)
      .then((v) => { if (!cancelled) { setView(v); setMasked(v.maskedEmail); } })
      .catch((err) => { if (!cancelled) fail(apiError(err, 'This link could not be opened'), 'fatal'); });
    return () => { cancelled = true; };
  }, [token, fail]);

  useEffect(() => {
    if (!view || authLoading || phase !== 'loading') return;
    setPhase(isAuthenticated || signedIn ? 'answer' : 'landing');
  }, [view, authLoading, isAuthenticated, signedIn, phase]);

  const decide = async (decision: 'approve' | 'decline') => {
    setBusy(decision); setError(null);
    try {
      setView(await api.extendDecide(token, decision));
    } catch (err) {
      const e = apiError(err, 'Your answer could not be saved');
      if (e.code === 'WRONG_ACCOUNT') { if (e.maskedEmail) setMasked(e.maskedEmail); setPhase('wrong'); return; }
      if (e.code === 'EXTEND_ANSWERED' || e.code === 'EXTEND_EXPIRED') { api.extendView(token).then(setView).catch(() => {}); }
      fail(e, 'answer');
    } finally { setBusy(null); }
  };

  const sendCode = async () => {
    setBusy('send'); setError(null);
    try {
      const r = await api.extendSendCode(token);
      setMasked(r.maskedEmail); setCode(''); lastSubmitted.current = null;
      setPhase('code');
      setTimeout(() => codeRef.current?.focus(), 0);
    } catch (err) { fail(apiError(err, 'The code could not be sent'), phase === 'code' ? 'code' : 'landing'); } finally { setBusy(null); }
  };

  const signIn = async (value: string) => {
    if (busy || value.length !== 6 || lastSubmitted.current === value) return;
    lastSubmitted.current = value;
    setBusy('code'); setError(null);
    try {
      const r = await api.extendSignIn(token, value);
      setView(r.view); setSignedIn(true); setPhase('answer');
    } catch (err) {
      const e = apiError(err, 'That code could not be checked');
      if (e.status === 404) { setPhase('invalid'); return; }
      setError(withWait(e)); setCode(''); lastSubmitted.current = null;
      setTimeout(() => codeRef.current?.focus(), 0);
    } finally { setBusy(null); }
  };

  const onCode = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void signIn(digits);
  };

  const switchAccount = async () => {
    setBusy('out');
    try { await logout(); } finally { setBusy(null); }
    setSignedIn(false); setError(null); setPhase('landing');
  };

  if (phase === 'invalid') {
    return (
      <CrewShell>
        <div className="flex flex-col gap-3 pt-6">
          <h1 className="m-0 text-[26px] font-bold leading-tight">This link doesn’t work any more</h1>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>The skipper may have cancelled the request. Your papers and the dates you chose are still in <Link to="/papers" className={C.link}>your papers</Link>.</p>
        </div>
      </CrewShell>
    );
  }
  if (phase === 'loading' || !view) return <CrewShell>{phase === 'fatal' ? <ErrorNote>{error}</ErrorNote> : <Loading />}</CrewShell>;

  const who = firstName(view.requesterName);
  const passage = passageLabel(view.passage.from, view.passage.to);
  const current = view.papers.map((p) => p.lastDay).sort()[0] ?? null;

  const papers = (
    <ul className="m-0 flex list-none flex-col gap-0 p-0">
      {view.papers.map((p) => (
        <li key={p.label} className="flex items-center justify-between gap-3 border-b border-[#1d3a70] py-3 text-base last:border-b-0">
          <span>{p.label}</span>
          <span className={`text-sm ${C.muted}`}>{p.active ? `until ${fmtDay(p.lastDay)}` : 'access ended'}</span>
        </li>
      ))}
    </ul>
  );

  // answered, replaced or expired: say so, and nothing to press
  if (view.status !== 'pending') {
    const text = view.status === 'approved' ? { h: 'Access extended', p: `${who} can see these papers until the end of ${fmtDayYear(view.untilOn)}. You can change the date or withdraw at any time.` }
      : view.status === 'declined' ? { h: 'You kept your date', p: `${who}’s access ends on the date you chose. We told ${who}.` }
        : view.status === 'replaced' ? { h: 'There’s a newer ask', p: `${who} sent a newer ask — use the link in the latest email.` }
          : { h: 'This ask has expired', p: `Nothing changed. ${who} can send a new one if they still need your papers.` };
    return (
      <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
        <div className="flex flex-col gap-3 pt-6">
          {view.status === 'approved' && <Tick className="h-8 w-8" />}
          <StepHeading focusKey={`done-${view.status}`}>{text.h}</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>{text.p}</p>
        </div>
        <ErrorNote>{error}</ErrorNote>
        <Link to="/papers" className={`flex min-h-[44px] items-center text-[15px] ${C.link} ${C.focus}`}>Open my papers</Link>
      </CrewShell>
    );
  }

  if (phase === 'wrong') {
    return (
      <CrewShell right={user?.email}>
        <div className="flex flex-col gap-3 pt-4">
          <StepHeading focusKey="wrong">This is for someone else</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>{who} sent it to <strong className="text-white">{masked}</strong>, but you’re signed in as <strong className="text-white">{user?.email}</strong>.</p>
        </div>
        <PrimaryButton onClick={() => void switchAccount()} busy={busy === 'out'}>Sign out and continue as {masked}</PrimaryButton>
      </CrewShell>
    );
  }

  if (phase === 'code') {
    return (
      <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
        <div className="flex flex-col gap-3 pt-6">
          <StepHeading focusKey="code" className="text-[28px]">Check your email</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>We sent a 6-digit code to <strong className="text-white">{masked}</strong>.</p>
        </div>
        <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); void signIn(code); }}>
          <div className="flex flex-col gap-2.5">
            <label htmlFor="extend-code" className="text-sm font-semibold text-[#c7d3e6]">Code</label>
            <input
              id="extend-code" ref={codeRef} name="code" value={code} onChange={(e) => onCode(e.target.value)}
              autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" maxLength={7} autoFocus
              aria-invalid={error ? true : undefined} disabled={busy === 'code'}
              className="h-[58px] w-full rounded-xl border border-[#2a4a86] bg-[#081a3b] px-4 text-center font-mono text-[28px] font-medium tracking-[0.5em] text-white focus:border-[#5aa9ff] focus:outline-none focus:ring-2 focus:ring-[#5aa9ff]"
            />
          </div>
          <ErrorNote>{error}</ErrorNote>
          <PrimaryButton type="submit" busy={busy === 'code'} disabled={code.length !== 6}>{busy === 'code' ? 'Checking…' : 'Continue'}</PrimaryButton>
        </form>
        <button type="button" onClick={() => void sendCode()} disabled={!!busy} className={`min-h-[44px] self-start text-left text-[15px] ${C.link} ${C.focus}`}>Send a new code</button>
      </CrewShell>
    );
  }

  // the ask itself: pending — answer (signed in) or sign in first
  return (
    <CrewShell right={view.requesterName ? `For ${view.requesterName}` : undefined}>
      <div className={`${C.card} overflow-hidden`}>
        <div className="flex flex-col gap-1 border-b border-[#1d3a70] bg-[#061430] px-5 py-4">
          <span className={C.label}>Keep access longer?</span>
          {passage && <span className="text-lg font-bold text-white">{passage}</span>}
        </div>
        <div className="flex flex-col gap-4 p-5">
          <StepHeading focusKey={phase} className="text-[22px]">{who} asks to keep access until {fmtDay(view.untilOn)}</StepHeading>
          <p className={`m-0 text-base leading-relaxed ${C.soft}`}>
            Hi {firstName(view.recipientName, 'there')}, {who} can see these papers{current ? <> until the end of <strong className="text-white">{fmtDay(current)}</strong></> : null}. Approving keeps them open until the end of <strong className="text-white">{fmtDayYear(view.untilOn)}</strong>.
          </p>
          {papers}
          <ErrorNote>{error}</ErrorNote>
          {phase === 'answer' ? (
            <div className="flex flex-col gap-2.5">
              <PrimaryButton onClick={() => void decide('approve')} busy={busy === 'approve'} disabled={!!busy}>{busy === 'approve' ? 'Saving…' : `Approve until ${fmtDay(view.untilOn)}`}</PrimaryButton>
              <SecondaryButton onClick={() => void decide('decline')} disabled={!!busy}>{busy === 'decline' ? 'Saving…' : 'Decline — keep my date'}</SecondaryButton>
            </div>
          ) : (
            <>
              <PrimaryButton onClick={() => void sendCode()} busy={busy === 'send'}>{busy === 'send' ? 'Sending a code…' : 'Sign in to answer'}</PrimaryButton>
              <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>We’ll email a 6-digit code to {masked} to check it’s you.</p>
            </>
          )}
        </div>
      </div>
      <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>Nothing changes unless you approve. Either way you can still change the date or withdraw at any time from your papers.</p>
    </CrewShell>
  );
}

export default ExtendAccessPage;
